import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Server,
  Siren,
  FileText,
  ScrollText,
  Users,
  DollarSign,
  Layers,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { NousVizMark } from '@/components/brand/NousVizMark';
import { Badge } from '@/components/ui/Badge';
import { Tooltip } from '@/components/ui/Tooltip';
import { useUI } from '@/lib/store';
import { useIncidentList } from '@/api/incidents';


type BadgeTone = 'neutral' | 'accent' | 'success' | 'warn' | 'danger';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: string;
  badgeTone?: BadgeTone;
  hint?: string;
}

function buildNav(openIncidents: number): { primary: NavItem[]; internal: NavItem[] } {
  return {
    primary: [
      { to: '/', label: 'Overview', icon: LayoutDashboard, end: true, hint: 'Health, alerts at a glance.' },
      { to: '/tenants', label: 'Customers', icon: Building2, hint: 'Customer organisations and their workspaces.' },
      { to: '/incidents', label: 'Incidents', icon: Siren, badge: openIncidents > 0 ? String(openIncidents) : undefined, badgeTone: 'warn', hint: 'Active outages and recent incident history.' },
      { to: '/logs', label: 'Logs', icon: FileText, hint: 'Cross-workspace application logs.' },
      { to: '/fleet', label: 'Infrastructure', icon: Server, hint: 'Regions, droplets, version distribution.' },
    ],
    internal: [
      { to: '/costs', label: 'Costs & revenue', icon: DollarSign, hint: 'Live infra cost per customer vs plan revenue.' },
      { to: '/plans', label: 'Plans', icon: Layers, hint: 'Plan presets — provisioning limits and licence contents.' },
      { to: '/staff', label: 'Staff & access', icon: Users, hint: 'NousViz operators authorised to use this admin.' },
      { to: '/audit', label: 'Audit log', icon: ScrollText, hint: 'Every action by NousViz staff across all customers.' },
    ],
  };
}

export function Sidebar() {
  const navOpen = useUI(s => s.navOpen);
  const setNavOpen = useUI(s => s.setNavOpen);
  const { data: incList = [] } = useIncidentList();
  const openIncidents = incList.filter(i => i.status !== 'resolved').length;
  const nav = buildNav(openIncidents);

  return (
    <>
      {navOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40 animate-fade-in"
          onClick={() => setNavOpen(false)}
        />
      )}
      <aside
        className={cn(
          'shrink-0 bg-bg-elevated border-r border-border flex flex-col',
          'fixed inset-y-0 left-0 z-50 w-60 transition-transform duration-200 ease-standard',
          'md:static md:translate-x-0 md:w-56',
          navOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
        onClick={() => setNavOpen(false)}
      >
        <div className="h-12 px-4 flex items-center border-b border-border gap-2">
          <NousVizMark className="h-3.5 text-fg shrink-0" />
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-fg-subtle border border-border rounded px-1.5 py-0.5">
            Admin
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); setNavOpen(false); }}
            className="md:hidden ml-auto w-7 h-7 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-2"
            aria-label="Close menu"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-3 py-3">
          <DeploymentBadge />
        </div>

        <nav className="flex-1 px-2 pb-4 space-y-5 overflow-y-auto">
          <NavSection items={nav.primary} />
          <NavSection heading="Internal" items={nav.internal} />
        </nav>

        <div className="px-4 py-3 border-t border-border text-2xs text-fg-subtle">
          v0.1 · admin.nousviz.cloud
        </div>
      </aside>
    </>
  );
}

function DeploymentBadge() {
  return (
    <div className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md">
      <span className="inline-flex w-1.5 h-1.5 rounded-full bg-[color:var(--success)] shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-fg truncate">NousViz Cloud</div>
        <div className="text-2xs text-fg-subtle truncate">Operator console</div>
      </div>
    </div>
  );
}

function NavSection({ heading, items }: { heading?: string; items: NavItem[] }) {
  return (
    <div>
      {heading && (
        <div className="px-2 mb-1 text-2xs uppercase tracking-wider font-semibold text-fg-subtle">
          {heading}
        </div>
      )}
      <ul className="space-y-0.5">
        {items.map(({ to, label, icon: Icon, end, badge, badgeTone, hint }) => {
          const navLink = (
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                  isActive
                    ? 'bg-surface-2 text-fg font-semibold'
                    : 'text-fg-muted hover:text-fg hover:bg-surface-2/60',
                )
              }
            >
              <Icon size={15} className="shrink-0" />
              <span className="truncate flex-1">{label}</span>
              {badge && <Badge tone={badgeTone ?? 'neutral'} className="px-1 h-4">{badge}</Badge>}
            </NavLink>
          );
          return (
            <li key={to}>
              {hint ? (
                <Tooltip side="bottom" content={<span>{hint}</span>} maxWidth={240}>
                  {navLink}
                </Tooltip>
              ) : (
                navLink
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
